/**
 * Form validation utilities for react-hook-form
 * Integrates existing validationUtils with react-hook-form
 */

import {
  validateFullName,
  validateEmail,
  validatePhoneNumber,
  formatPhoneNumber,
  validateFirstName,
  validateLastName,
} from "./validationUtils";
import { parseLocationString } from "./locationUtils";

/**
 * Signup form data type
 */
export interface SignupFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  location: string;
  gender: string;
  discoveryChannel: string;
  partnerReferralCode: string;
}

/**
 * Google profile completion form data type
 */
export interface GoogleProfileFormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  gender: string;
  location: string;
  discoveryChannel: string;
  partnerReferralCode: string;
}

/**
 * Validation rules for signup form
 */
export const signupValidationRules = {
  name: {
    validate: (value: string) => {
      const error = validateFullName(value);
      return error || true; // Return true if valid, error message if invalid
    },
  },
  email: {
    validate: (value: string) => {
      const error = validateEmail(value);
      return error || true;
    },
  },
  phone: {
    validate: (value: string) => {
      const error = validatePhoneNumber(value);
      return error || true;
    },
  },
  password: {
    required: "Password is required",
    minLength: {
      value: 6,
      message: "Password must be at least 6 characters long",
    },
  },
  confirmPassword: {
    validate: (value: string, formValues: SignupFormData) => {
      if (value !== formValues.password) {
        return "Passwords do not match";
      }
      return true;
    },
  },
  gender: {
    required: "Gender is required",
  },
  location: {
    validate: (value: string) => {
      if (!value) return "Location is required";
      const parsed = parseLocationString(value);
      if (!parsed.state || parsed.state === "") {
        return "Please select your state";
      }
      if (!parsed.lga || parsed.lga === "") {
        return "Please select a specific area (LGA) in addition to the state";
      }
      return true;
    },
  },
  discoveryChannel: {
    // Optional field, no validation needed
  },
};

/**
 * Validation rules for Google profile completion form
 */
export const googleProfileValidationRules = {
  firstName: {
    validate: (value: string) => {
      const error = validateFirstName(value);
      return error || true;
    },
  },
  lastName: {
    validate: (value: string) => {
      const error = validateLastName(value);
      return error || true;
    },
  },
  phoneNumber: {
    validate: (value: string) => {
      const error = validatePhoneNumber(value);
      return error || true;
    },
  },
  gender: {
    required: "Gender is required",
    validate: (value: string) => {
      if (!["male", "female", "Male", "Female"].includes(value)) {
        return "Please select a valid gender";
      }
      return true;
    },
  },
  location: {
    validate: (value: string) => {
      if (!value) return "Location is required";
      const parsed = parseLocationString(value);
      if (!parsed.lga) {
        return "Please select a state and LGA from the location picker";
      }
      return true;
    },
  },
  discoveryChannel: {
    // Optional field, no validation needed
  },
};

/**
 * Helper to format phone number on change
 */
export { formatPhoneNumber };
